import * as React from 'react';
import { Formik, FormikConfig } from 'formik';
import { makeValidator } from 'mandle';
import gql from 'graphql-tag';
import { useMutation } from 'urql';
import {
  RootMutationType,
  RootMutationTypeShareMatchDetailsArgs,
  FullMatchFragment,
} from '../../generated/graphql';
import { Input } from '../../components/Input';
import { makeInputPropsMaker } from '../../utils/inputPropsMaker';
import { TextArea } from '../../components/TextArea';
import { required, emailAddress, maxLength } from '../../validators';
import { SubmitButton } from '../../components/SubmitButton';
import { ButtonLink } from '../../components/Button';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { makeWordList } from '../../utils/makeWordList';

interface Props {
  match: FullMatchFragment;
}

interface Fields {
  email: string;
  comment: string;
}

const initialValues = { email: '', comment: '' };

const validate = makeValidator<Fields>({
  email: [required, emailAddress],
  comment: [maxLength(1000)],
});

const SHARE_MATCH_DETAILS = gql`
  mutation ShareMatchDetails($input: ShareMatchInput!) {
    shareMatchDetails(input: $input) {
      validation {
        key
        reason
      }
    }
  }
`;

export const ShareMatchEmail: React.FC<Props> = ({ match }) => {
  const [shareMatchResponse, shareMatch] = useMutation<
    Pick<RootMutationType, 'shareMatchDetails'>,
    RootMutationTypeShareMatchDetailsArgs
  >(SHARE_MATCH_DETAILS);

  const analytics = useAnalytics();

  const onSubmit: FormikConfig<Fields>['onSubmit'] = (values) => {
    analytics.track('Form Submitted', {
      form: 'share_match_email',
      match: match.id,
    });
    shareMatch({ input: { ...values, id: match.id } }).then(({ data }) => {
      if (
        data &&
        data.shareMatchDetails &&
        !data.shareMatchDetails.validation
      ) {
        analytics.track('Data Submit Succeeded', {
          form: 'share_match_email',
          match: match.id,
        });
      }
      if (data && data.shareMatchDetails && data.shareMatchDetails.validation) {
        analytics.track('Data Submit Failed', {
          form: 'share_match_email',
          match: match.id,
          errors: data.shareMatchDetails.validation,
        });
      }
    });
  };

  if (
    shareMatchResponse.data &&
    shareMatchResponse.data.shareMatchDetails &&
    !shareMatchResponse.data.shareMatchDetails.validation
  ) {
    return <ShareMatchSuccess matchId={match.id} />;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={validate}
    >
      {(formikProps) => {
        const getInputProps = makeInputPropsMaker(formikProps);

        const matchItemsSet = makeWordList(
          ['date & time']
            .concat(match.location ? ['location'] : [])
            .concat(match.kitItems.length ? ['your chosen kit'] : []),
        );
        return (
          <form className="space-y-4" onSubmit={formikProps.handleSubmit}>
            <p>
              Send a match summary to opposition or match officials. This will
              include {matchItemsSet}.
            </p>
            <Input
              type="email"
              label="Email address"
              {...getInputProps('email')}
              placeholder="captain@opposition.com"
            />
            <TextArea
              {...getInputProps('comment')}
              label="Message (optional)"
              placeholder="Add a message"
            ></TextArea>

            <p>We'll send you a copy as well.</p>

            <SubmitButton
              type="submit"
              icon="chevron-right"
              loading={shareMatchResponse.fetching}
            >
              Send
            </SubmitButton>

            <ButtonLink to={`/m/${match.id}`} kind="plain">
              Cancel
            </ButtonLink>
          </form>
        );
      }}
    </Formik>
  );
};

interface ShareMatchSuccessProps {
  matchId: string;
}

const ShareMatchSuccess: React.FC<ShareMatchSuccessProps> = ({ matchId }) => {
  return (
    <div className="text-center space-y-6">
      <h1 className="text-2xl font-medium">Match details sent</h1>
      <ButtonLink to={`/m/${matchId}`}>Back to match</ButtonLink>
    </div>
  );
};
